import _ from 'lodash';
import React, { useState } from 'react';
import PropTypes from 'prop-types';

import AttachmentModal from 'components/attachments/attachment_modal';
import AttachmentPreviewOpener from 'components/lib/attachment/attachment_preview_opener';
import AttachmentsDownloadAllContainer from 'components/lib/attachment/attachments_download_all_container';
import AttachmentStatus from 'models/attachment/attachment_status';
import GenericAttachment from 'components/lib/attachment/generic_attachment';
import { getSmsAttachmentUrl } from 'scripts/domain/services/attachments';
import { HandleHeightChange } from 'components/customer/conversation_history/virtualized/list_item';
import InlineImageAttachment from 'components/lib/attachment/inline_image_attachment';
import InlineVideoAttachment from 'components/lib/attachment/inline_video_attachment';
import SmsMessageAttachment from 'models/sms_message_attachment';
import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';

const SmsMessageAttachmentsContainer = AttachmentPreviewOpener(SmsMessageAttachments);

function SmsMessageAttachments({ attachments, itemId, orgId }) {
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const isFeatureEnabled = useIsFeatureEnabled();
  if (_.isEmpty(attachments)) {
    return null;
  }

  const videoPreviewEnabled = isFeatureEnabled('videoAttachmentPreview');
  let [inlineAttachments, genericAttachments] = _.partition(
    attachments,
    a =>
      !a.isRedacted && (a.isImage() || (videoPreviewEnabled && a.isVideo())) && a.status === AttachmentStatus.AVAILABLE
  );
  let availableAttachments = _.filter(
    inlineAttachments.concat(genericAttachments),
    a => a.status === AttachmentStatus.AVAILABLE
  );

  return (
    <React.Fragment>
      <div className="itemAttachments">
        <ul className="attachments-inlineImageAttachments">{_.map(inlineAttachments, renderInlineAttachment)}</ul>
        <div className="genericAttachments">
          {_.map(genericAttachments, renderGenericAttachment)}
          <AttachmentsDownloadAllContainer attachments={availableAttachments} itemId={itemId} pathPrefix="sms" />
        </div>
      </div>
      {previewAttachment && (
        <AttachmentModal
          attachment={previewAttachment}
          attachments={availableAttachments}
          setAttachment={setPreviewAttachment}
          url={getDownloadUrl(previewAttachment)}
        />
      )}
    </React.Fragment>
  );

  function renderGenericAttachment(attachment, index) {
    return (
      <GenericAttachment
        attachment={attachment}
        key={attachment.id}
        onClick={(attachment.status === AttachmentStatus.AVAILABLE && openAttachmentPreview) || undefined}
      />
    );

    function openAttachmentPreview() {
      setPreviewAttachment(attachment);
    }
  }

  function renderInlineAttachment(attachment, index) {
    return attachment.isVideo()
      ? renderInlineVideoAttachment(attachment, index)
      : renderInlineImageAttachment(attachment, index);
  }

  function renderInlineImageAttachment(attachment, index) {
    return (
      <HandleHeightChange key={attachment.id}>
        {({ onHeightChange }) => (
          <InlineImageAttachment
            attachment={attachment}
            inlinePreviewImageUrl={getPreviewUrl()}
            key={attachment.id}
            onClick={openAttachmentPreview}
            onLoad={onHeightChange}
          />
        )}
      </HandleHeightChange>
    );

    function getPreviewUrl() {
      let imageUrl = getDownloadUrl(attachment);
      return `${imageUrl}?size=preview`;
    }

    function openAttachmentPreview() {
      setPreviewAttachment(attachment);
    }
  }

  function renderInlineVideoAttachment(attachment, index) {
    return (
      <HandleHeightChange key={attachment.id}>
        {({ onHeightChange }) => (
          <InlineVideoAttachment
            attachment={attachment}
            inlinePreviewVideoUrl={getDownloadUrl(attachment)}
            key={attachment.id}
            onClick={openAttachmentPreview}
            onLoad={onHeightChange}
          />
        )}
      </HandleHeightChange>
    );

    function openAttachmentPreview() {
      setPreviewAttachment(attachment);
    }
  }

  function getDownloadUrl(a) {
    return getSmsAttachmentUrl(orgId, a);
  }
}

SmsMessageAttachmentsContainer.propTypes = {
  attachments: PropTypes.arrayOf(PropTypes.instanceOf(SmsMessageAttachment)).isRequired,
  itemId: PropTypes.string.isRequired,
  onHeightChange: PropTypes.func,
};

SmsMessageAttachments.propTypes = {
  ...AttachmentPreviewOpener.childPropTypes,
  ...SmsMessageAttachmentsContainer.propTypes,
};

export { SmsMessageAttachments };
export default SmsMessageAttachmentsContainer;
